// ── Ordinex — Offline outbox + connectivity UI ───────────────────────────────
// Lets foremen complete & submit safety forms with no signal on-site. The PDF is
// generated locally (works offline), stored in an IndexedDB "outbox", and sent
// automatically the moment connectivity returns.
//
// Scope: single-shot PDF+email forms with NO structured dashboard sync, so a
// replay is just: upload PDF → log submission row → send email. Production
// Report and Weekly Timesheet are intentionally excluded (they expand into
// per-day dashboard rows and need a connection).
//
// Loaded before app.js; app.js calls saveSubmissionOffline()/showOfflineSavedScreen()
// from handleSubmit, and auth.js calls flushOutbox() after a successful login.

// Forms that can be completed and queued with no signal. Simple forms replay as
// PDF + one submissions row + email. Production Report and Weekly Timesheet also
// replay a structured per-day fan-out (Onda 2b), and Extra Work Order replays a
// parent + labour/materials + photos (Onda 2d) — the record carries syncKind +
// fields (+ photos/signatures) and the replay runs the SAME idempotent sync, so
// a re-replay never duplicates. The EWO number is assigned at replay time via
// next_ewo_number (it can't be known offline). Site Photos Only replays via its
// own storage-upload path (no PDF, see saveSitePhotosOffline) and is therefore
// the one submission type deliberately absent from this list. Request delivery
// and Material pickup are not forms either: js/delivery.js queues them as
// syncKind 'delivery' (slip photos included) and they replay through its own
// sendDeliveryRecord, idempotent by the row id made on the phone.
const OFFLINE_FORMS = [
  'Daily Tailgate',
  'Weekly Toolbox Talk',
  'Incident Report',
  'Telehandler Inspection',
  'Forklift Inspection',
  'E-Pallet Jack Inspection',
  'Scaffolding Inspection',
  'Safety Inspection',
  'QAQC - Foreman',
  'Hazard Observation',
  'Production Report',
  'Weekly Timesheet',
  'Extra Work Order',

  // ── Drilling & Shotcrete pack ─────────────────────────────────────────────
  // All 17 replay through the generic path (PDF + one submissions row). They
  // are plain schema-driven forms — text/number/select/radio/checklist/table
  // fields plus a signature, with photos embedded in the PDF exactly like the
  // Incident Report. None carries an online dependency: no server-assigned
  // number (the EWO's next_ewo_number), no separate upload path (Site Photos
  // Only), no structured per-day fan-out, and none is a safety-email form.
  'Drill Rig Daily Log',
  'Air Compressor Daily Log',
  'Concrete Pump Daily Log',
  'Dry-Mix Pump Daily Log',
  'Grout Plant Daily Log',
  'Truck Monthly Inspection',
  'Equipment Maintenance Record',
  'Incident Investigation',
  'First Aid Assessment',
  'Disciplinary Report',
  'Site Orientation Record',
  'Daily Site Report',
  'Anchor Installation Log',
  'Anchor Test Record',
  'Shotcrete Placement Record',
  'Shotcrete Test Panel Log',
  'Grout Batch Log',

  // ── Hot work (neutral) ────────────────────────────────────────────────────
  // Single-shot: simple fields + checklist + signature → PDF + one submissions
  // row + safety email on replay. No server-assigned number, no fan-out.
  'Hot Work & Fire Watch Record',

  // Heavy equipment (neutral) — filled at the machine, wherever it is parked.
  'Heavy Equipment Daily Log',
  // MEWP (neutral) — filled at the lift, often deep inside a building.
  'MEWP Daily Log',

  // ── Excavation & Sitework pack ────────────────────────────────────────────
  // Same single-shot path — and the form a crew is most likely to fill with
  // no signal, since civil work happens where the towers aren't.
  'Excavation Daily Inspection',

  // ── Mechanical pack ───────────────────────────────────────────────────────
  // Same single-shot path. Mechanical rooms and shafts are the other place a
  // phone has no bars, so both matter offline.
  'Pressure Test Record',
  'Refrigerant Service & Leak Log',

  // ── Demolition & Environmental pack ───────────────────────────────────────
  'Pre-Demolition Readiness Check',
  'Abatement Containment Daily Log',
];

// Give up replaying a record after this many failed attempts — it stays in the
// outbox flagged "stuck" so the foreman's work is never silently discarded.
const OFFLINE_MAX_ATTEMPTS = 10;

// ═══════════════════════════════════════════════════════════════════════════════
//  IndexedDB outbox
// ═══════════════════════════════════════════════════════════════════════════════
const _OFFLINE_DB    = 'ordinex-offline';
const _OFFLINE_STORE = 'outbox';

function _openOutbox() {
  return new Promise((resolve, reject) => {
    let req;
    try { req = indexedDB.open(_OFFLINE_DB, 1); }
    catch (e) { reject(e); return; }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(_OFFLINE_STORE)) {
        db.createObjectStore(_OFFLINE_STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

function _outboxAdd(record) {
  return _openOutbox().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(_OFFLINE_STORE, 'readwrite');
    const r  = tx.objectStore(_OFFLINE_STORE).add(record);
    r.onsuccess = () => resolve(r.result);
    r.onerror   = () => reject(r.error);
  }));
}
function _outboxAll() {
  return _openOutbox().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(_OFFLINE_STORE, 'readonly');
    const r  = tx.objectStore(_OFFLINE_STORE).getAll();
    r.onsuccess = () => resolve(r.result || []);
    r.onerror   = () => reject(r.error);
  }));
}
function _outboxDelete(id) {
  return _openOutbox().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(_OFFLINE_STORE, 'readwrite');
    const r  = tx.objectStore(_OFFLINE_STORE).delete(id);
    r.onsuccess = () => resolve();
    r.onerror   = () => reject(r.error);
  }));
}
function _outboxPut(record) {
  // put() with the same keyPath id updates in place (attempts/stuck bookkeeping)
  return _openOutbox().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(_OFFLINE_STORE, 'readwrite');
    const r  = tx.objectStore(_OFFLINE_STORE).put(record);
    r.onsuccess = () => resolve();
    r.onerror   = () => reject(r.error);
  }));
}
async function outboxCount() {
  try { return (await _outboxAll()).length; } catch { return 0; }
}

// Whose record is it? The outbox belongs to the device, the session to a person: a record is
// replayed, counted and shown only under the account that queued it, because the server stamps
// punched_by from the session that sends. A record with no userId predates the field and belongs
// to whoever is signed in; with nobody signed in yet only those match.
function _isMine(rec) {
  const uid = window.currentUser && window.currentUser.id;
  return !rec.userId || rec.userId === uid;
}

// A punch the signed-in foreman made for a worker (contract 3.4): a crew punch or a day sheet's correction.
function _isCrewPunch(r) {
  return r.syncKind === 'punch' && (r.punch?.source === 'crew' || r.punch?.source === 'correction');
}

// The hold of a stuck punch (see flushOutbox): a worker with a punch refused for good has his later
// punches held back behind it — not sent, not "sending", waiting with it for the foreman. One rule for
// every reader: the flush's skip list, the pending punches, the pill and the foreman sheet.
function _punchHold(recs) {
  const workers = new Set(recs.filter(r => r.syncKind === 'punch' && r.stuck).map(r => r.workerId));
  return { workers, isHeld: r => r.syncKind === 'punch' && !r.stuck && !r.waiting && workers.has(r.workerId) };
}

// The Monday of a work date, as 'YYYY-MM-DD' — one shape for the whole app.
//
// It used to be stored as a raw Date. IndexedDB keeps Date objects intact, so
// the watchdog's dedup key came out as 'Langley|Mon Aug 11 2025 00:00:00
// GMT-0700 (Pacific Daylight Time)' while the lookup asked for
// 'Langley|2025-08-11'. It never matched, so a queued repair was invisible and
// the watchdog queued another one on every pass until the attempt cap turned
// into a toast. The same Date also went into submissions.week_of on the replay
// path, where it serialised to UTC and could land a day off east of Greenwich.
function _weekOfIso(dateIso) {
  if (!dateIso) return null;
  if (typeof window._weekStartFor !== 'function' ||
      typeof window._dateFromWeekStart !== 'function') return null;
  try { return window._dateFromWeekStart(window._weekStartFor(dateIso), 0); }
  catch { return null; }
}

// Records queued before that fix still hold a Date. Normalise on read so a
// device that upgrades mid-week does not carry the mismatch forward.
function _weekOfKey(weekOf) {
  if (!weekOf) return null;
  if (typeof weekOf === 'string') return weekOf.slice(0, 10);
  if (weekOf instanceof Date && !isNaN(weekOf)) {
    const y = weekOf.getFullYear();
    return y + '-' + String(weekOf.getMonth() + 1).padStart(2, '0') + '-' +
           String(weekOf.getDate()).padStart(2, '0');
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Save a submission for later (called from app.js handleSubmit)
// ═══════════════════════════════════════════════════════════════════════════════
async function saveSubmissionOffline(s, pdfBuffer, pdfFilename) {
  // generatePDF returns an ArrayBuffer; normalise just in case a typed array slips in.
  let buf = pdfBuffer;
  if (pdfBuffer && pdfBuffer.buffer instanceof ArrayBuffer && !(pdfBuffer instanceof ArrayBuffer)) {
    buf = pdfBuffer.buffer.slice(pdfBuffer.byteOffset, pdfBuffer.byteOffset + pdfBuffer.byteLength);
  }
  // The form is always filed under the account that queued it — no override
  // exists any more. The Production Report crew (admin picking a subcontract
  // crew) rides along separately so the replay can still land those units on
  // the sub's production row; it never touches who authored the form.
  const effId   = (window.currentUser && window.currentUser.id) || null;
  const effName = ((window.currentProfile && window.currentProfile.full_name) || s.foremanName || '');

  // Trade pack in effect right now — state.project is still the queued form's
  // project at this point, so this must be computed at SAVE time (not replay
  // time, when the foreman may have moved to a different project/pack).
  const tradePack = typeof activeTradePack === 'function' ? activeTradePack() : null;
  const _schema = window.Ordinex?.forms?.resolve(s.submissionType, tradePack) || null;
  // Structured payload captured at queue time so replay matches the online
  // insert; client_uuid dedups a double replay. The record shape lives in the
  // testable core module (js/core/offline-record.js) — offline.js just supplies
  // the browser-derived values.
  const record = window.Ordinex.offlineRecord.build({
    submissionType: s.submissionType, project: s.project, foremanName: s.foremanName,
    date: s.date, workersOnSite: s.workersOnSite, sendToSafety: !!s.sendToSafety,
    csoContact: s._csoContact || null,
    effUserId: effId, effUserName: effName,
    crewForemanId: s.productionCrewId || null,
    crewForemanName: s.productionCrewName || '',
    orgId: (window.currentOrg && window.currentOrg.id) || null,
    pdf: buf, pdfFilename, signed: !!s.signature,
    clientUuid: s._clientUuid,
    formKey: window.Ordinex?.forms?.keyFor(s.submissionType) || null,
    weekOf: _weekOfIso(s.date) || (s.weekOf || null),
    weekMode: s._weekMode || null,
    schemaId: _schema?.id || null, schemaVersion: _schema?.version || null,
    tradePack,
    data: (typeof _buildSubmissionData === 'function') ? _buildSubmissionData() : (s.fields || {}),
    // Structured forms: the replay reconstructs `s` from these to run the SAME
    // idempotent sync. Production/Timesheet (Onda 2b) fan out per-day rows; Extra
    // Work Order (Onda 2d) writes the parent + labour/materials + photos and gets
    // its number assigned at replay time (next_ewo_number can't run offline).
    syncKind: s.submissionType === 'Production Report' ? 'production'
            : s.submissionType === 'Weekly Timesheet'  ? 'timesheet'
            : s.submissionType === 'Extra Work Order'  ? 'ewo' : null,
    fields: s.fields || null,
    // EWO extras — photos (already compressed at add-time, Onda 2a) + the two
    // signatures the parent row stores. Null for the other forms.
    photos: s.submissionType === 'Extra Work Order'
      ? (Array.isArray(s.photos) ? s.photos.map(f => ({
          blob: f, name: (f && f.name) || 'photo.jpg', type: (f && f.type) || 'image/jpeg',
        })) : [])
      : null,
    signature:   s.submissionType === 'Extra Work Order' ? (s.signature   || null) : null,
    gcSignature: s.submissionType === 'Extra Work Order' ? (s.gcSignature || null) : null,
    emailsSafety: typeof _isSafetyEmailForm === 'function'
      ? _isSafetyEmailForm(s.submissionType) : undefined,
  });
  await _outboxAdd(record);
  _requestBackgroundSync();
  _renderOfflineUI();
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Site Photos offline (Onda 2c) — no PDF; the record carries the compressed
//  photo blobs + captions, and _replaySitePhotos uploads them on reconnect.
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
//  Background Sync (PWA item 2) — Android sends the queue from the pocket.
//  The page mirrors just enough session for sw.js to replay GENERIC records
//  (PDF + row + email) via plain REST when connectivity returns with the app
//  CLOSED. The worker never refreshes tokens (rotation would corrupt the
//  page's stored session) — an expired mirror means the queue simply waits
//  for the next app open, which is exactly today's behaviour. iOS has no
//  Background Sync at all; every call here no-ops there.
// ═══════════════════════════════════════════════════════════════════════════════
function _requestBackgroundSync() {
  try {
    navigator.serviceWorker?.ready
      .then(reg => reg.sync?.register('ordinex-outbox'))
      .catch(() => {});
  } catch (_) { /* unsupported — app-open flush covers it */ }
}

function _writeSessionMirror(session) {
  const put = (val) => new Promise((resolve) => {
    let req;
    try { req = indexedDB.open('ordinex-session', 1); } catch (_) { resolve(); return; }
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains('kv')) req.result.createObjectStore('kv', { keyPath: 'key' });
    };
    req.onerror = () => resolve();
    req.onsuccess = () => {
      try {
        const tx = req.result.transaction('kv', 'readwrite');
        if (val) tx.objectStore('kv').put(val); else tx.objectStore('kv').delete('current');
        tx.oncomplete = tx.onerror = () => resolve();
      } catch (_) { resolve(); }
    };
  });
  if (!session) return put(null);
  return put({
    key: 'current',
    accessToken: session.access_token,
    expiresAt: session.expires_at || 0,          // unix seconds
    userId: session.user?.id || null,
    supabaseUrl: (typeof sbClient !== 'undefined' && sbClient?.supabaseUrl) || null,
    anonKey: (typeof sbClient !== 'undefined' && sbClient?.supabaseKey) || null,
  });
}

function _initSessionMirror() {
  try {
    sbClient.auth.onAuthStateChange((_e, session) => { _writeSessionMirror(session); });
    sbClient.auth.getSession().then(({ data }) => {
      if (data?.session) _writeSessionMirror(data.session);
    }).catch(() => {});
  } catch (_) { /* no client on this page — nothing to mirror */ }
}

async function saveSitePhotosOffline(s, spPhotos) {
  const clientUuid = s._clientUuid || (crypto?.randomUUID?.() || String(Date.now()));
  const photos = (spPhotos || []).map(p => ({
    blob: p.file, caption: p.caption || '',
    name: (p.file && p.file.name) || 'photo.jpg',
    type: (p.file && p.file.type) || 'image/jpeg',
  }));
  const effId   = (window.currentUser && window.currentUser.id) || null;
  const effName = ((window.currentProfile && window.currentProfile.full_name) || s.foremanName || '');
  const tradePack = typeof activeTradePack === 'function' ? activeTradePack() : null;
  const record = window.Ordinex.offlineRecord.build({
    submissionType: 'Site Photos Only', project: s.project,
    syncKind: 'sitephotos', photos, pdf: null,
    effUserId: effId, effUserName: effName,
    orgId: (window.currentOrg && window.currentOrg.id) || null,
    clientUuid, tradePack,
  });
  await _outboxAdd(record);
  _requestBackgroundSync();   // structured — the worker wakes a client for it
  _renderOfflineUI();
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Replay one queued submission — ATOMIC order (the W5d fix):
//    1. PDF upload (best-effort; upsert so a retry can't 409)
//    2. submissions INSERT — the gate. client_uuid UNIQUE dedups a double
//       replay (23505 = already replayed = success). Any other failure throws,
//       keeping the record queued. Signed forms insert with locked_at so the
//       row is immutable from birth.
//    3. Email (best-effort) — it NEVER gates the queue. The DB row is the
//       source of truth; a mail hiccup must not lose or re-queue field work.
//  The old order let an email success dequeue a record whose DB insert had
//  silently failed — the foreman's work vanished. Never again.
// ═══════════════════════════════════════════════════════════════════════════════
async function _replayTimeOff(rec) {
  // one INSERT with the request's own id (the device made it): a replay of a request that already
  // landed meets its own primary key, and that is the same request, not a second one
  // A refusal goes through W3's classifier like a decision's: the pkey 23505 is done, a refusal for good (42501, 23514)
  // is stuck_now — kept, said once, never ten tries and "contact support" (contract item 14)
  const { error, status } = await _withNetTimeout(sbClient.from('time_off_requests').insert(rec.request), 'timeoff replay');
  if (error) {
    const r = _classifyPunchError(error, rec, status);
    if (r.action === 'done') return;
    if (r.action === 'done_toast') { _toast(r.toast, 'info'); return; }
    throw Object.assign(new Error(error.message), { punch: r });
  }
  _notifyTimeOff(rec.request.status === 'pending' ? 'new' : 'decided', rec.request.id);
}

// A foreman's Approve / Deny (syncKind 'timeoff_decision'): one UPDATE of status + note; the server stamps who and when.
// A refusal goes through W3's classifier: already decided by somebody else -> its toast, done; a payroll-final week
// waits for the office (task-5 R3). No row back with no error = RLS no longer lets him decide it (the crewman left his
// crew): said once and dropped, never a "decided" push (task-5 R4).
async function _replayTimeOffDecision(rec) {
  const { data, error } = await _withNetTimeout(sbClient.from('time_off_requests')
    .update(rec.decision).eq('id', rec.requestId).select('id'), 'timeoff decision replay');
  if (error) {
    const r = _classifyPunchError(error, rec);
    if (r.action === 'done') return;
    if (r.action === 'done_toast') { _toast(r.toast, 'info'); return; }
    throw Object.assign(new Error(error.message), { punch: r });
  }
  if (!data?.length) { _toast('That request is no longer yours to decide', 'info'); return; }
  _notifyTimeOff('decided', rec.requestId);
}

// Fire-and-forget: the row is the truth, a push that fails never holds the queue.
function _notifyTimeOff(type, request_id) {
  sbClient.functions.invoke('notify-time-off', { body: { type, request_id } }).catch(e => console.warn('[timeoff] notify failed:', e));
}

async function _replaySitePhotos(rec) {
  const orgId = rec.orgId || (window.currentOrg && window.currentOrg.id) || null;
  const { data: project } = await sbClient.from('projects')
    .select('id').eq('name', rec.project).maybeSingle();
  if (!project) throw new Error('site photos replay: project not found: ' + rec.project);
  // Uploads first (idempotent: deterministic paths + upsert + documents dedup),
  // then the submission gate — so a re-replay never duplicates.
  if (typeof window.uploadSitePhotos === 'function') {
    await window.uploadSitePhotos({
      photos: rec.photos || [], projectId: project.id, orgId,
      uploadedBy: rec.userId, clientUuid: rec.clientUuid,
    });
  }
  const { error: insErr } = await sbClient.from('submissions').insert({
    foreman_id: rec.userId, foreman_name: rec.userName,
    project_name: rec.project, submission_type: 'Site Photos Only',
    trade_pack: rec.tradePack ?? null,
    client_uuid: rec.clientUuid || null,
  });
  if (insErr && insErr.code !== '23505') {
    throw new Error('site photos submission insert failed: ' + insErr.message);
  }
}

// ── Clock punches (check in / check out) ─────────────────────────────────────
// A queued punch is one INSERT into time_punches, stamped sent_at at the moment it goes.
// The database owns the rules (who is in, who is out, which week is closed), so the replay
// just asks and lets the answer (classifier table: contract 3.4) decide what happens:
//   landed, already there, or somebody else already did it -> resolve (flushOutbox deletes it)
//   only a foreman can fix it, a closed week, or a retry may work -> throw, with the
//   classification on `.punch` for flushOutbox to read.
async function _replayPunch(rec) {
  // the tap's 15 s deadline (js/app.js): a request that hangs must not hold the whole flush
  const { error, status } = await _withNetTimeout(sbClient.from('time_punches')
    .insert({ ...rec.punch, sent_at: new Date().toISOString() }), 'punch replay');
  if (!error) return;
  const result = _classifyPunchError(error, rec, status);
  if (result.action === 'done') return;
  if (result.action === 'done_toast') { _toast(result.toast, 'info'); return; }
  throw Object.assign(new Error(error.message), { punch: result });
}

function _classifyPunchError(error, rec, status) {
  return window.Ordinex.offlineRecord.classifyPunch(error, rec, window.Ordinex.timeClock?.fmtTime, status);
}

async function _replayRecord(rec) {
  if (rec.syncKind === 'punch') return _replayPunch(rec);
  if (rec.syncKind === 'timeoff') return _replayTimeOff(rec);
  if (rec.syncKind === 'timeoff_decision') return _replayTimeOffDecision(rec);
  if (rec.syncKind === 'sitephotos') return _replaySitePhotos(rec);
  // Request delivery / Material pickup: the same send the Send tap runs (js/delivery.js), idempotent by the row id.
  if (rec.syncKind === 'delivery') return sendDeliveryRecord(rec);
  if (rec.syncOnly) return _replaySyncOnly(rec);
  const pdfBuffer = rec.pdf;
  const orgId = rec.orgId || (window.currentOrg && window.currentOrg.id) || null;
  if (!rec.userId) throw new Error('queued record has no user — cannot replay');

  // 1. PDF to Storage (best-effort — the row matters more than the attachment).
  let pdfStorageUrl = null, pdfStoragePath = null;
  try {
    if (orgId) {
      // Same clean-up as buildPDFFilename(), for records queued by an older
      // build: one plain file name, so the database accepts the path.
      const safeFile = String(rec.pdfFilename || 'form.pdf').replace(/[^A-Za-z0-9'._-]+/g, '-').replace(/\.{2,}/g, '.');
      const fileName = `${orgId}/${rec.userId}/${safeFile}`;
      const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
      const { error: upErr } = await sbClient.storage
        .from('form-submissions')
        .upload(fileName, blob, { contentType: 'application/pdf', upsert: true });
      if (!upErr) {
        pdfStoragePath = fileName;
        const { data } = await sbClient.storage
          .from('form-submissions')
          .createSignedUrl(fileName, 60 * 60 * 24 * 365);
        pdfStorageUrl = data?.signedUrl || null;
      }
    }
  } catch (e) { console.warn('[offline] storage upload failed on replay:', e); }

  // 2. The gate: submission row with idempotency key.
  const { data: insRow, error: insErr } = await sbClient.from('submissions').insert({
    foreman_id:      rec.userId,
    foreman_name:    rec.userName,
    project_name:    rec.project,
    submission_type: rec.submissionType,
    week_of:         _weekOfKey(rec.weekOf),
    form_key:        rec.formKey || null,
    schema_id:       rec.schemaId || null,
    schema_version:  rec.schemaVersion || null,
    trade_pack:      rec.tradePack ?? null,
    data:            rec.data || {},
    // Email ledger (2026-09-01) — same stamp the online insert makes, so the
    // 15-min sweeper covers a replayed form even if the email call below dies.
    safety_email_status: (typeof _isSafetyEmailForm === 'function'
      ? _isSafetyEmailForm(rec.submissionType) : true) ? 'pending' : 'not_required',
    client_uuid:     rec.clientUuid || null,
    pdf_url:         pdfStorageUrl,
    pdf_path:        pdfStoragePath,
    locked_at:       rec.signed ? new Date().toISOString() : null,
  }).select('id').maybeSingle();
  if (insErr && insErr.code !== '23505') {
    // 23505 = duplicate client_uuid = an earlier replay already landed this row.
    throw new Error('submissions insert failed: ' + insErr.message);
  }

  // 2b. Structured per-day fan-out (Production Report / Weekly Timesheet). Resolve
  // the submission id (fresh insert, else re-fetch on a duplicate replay) so the
  // per-day rows link to it, then run the SAME idempotent sync. Best-effort like
  // the online path: the submission row + PDF are the source of truth, so a sync
  // hiccup never loses field work (and a re-replay re-upserts, no duplicates).
  let submissionId = insRow?.id || null;
  if (!submissionId && rec.clientUuid) {
    const { data: existing } = await sbClient.from('submissions')
      .select('id').eq('client_uuid', rec.clientUuid).maybeSingle();
    submissionId = existing?.id || null;
  }
  if (rec.syncKind) {
    // Always spell out the crew keys (null, not undefined) so the production
    // sync can't fall through to whatever form the user happens to have open
    // in this session. Records queued before the field existed replay as
    // "own crew", which is what they were.
    const s = { project: rec.project, date: rec.date, fields: rec.fields || {},
                _effForemanId: rec.userId, _effForemanName: rec.userName,
                _crewForemanId: rec.crewForemanId || null,
                _crewForemanName: rec.crewForemanName || '',
                photos: rec.photos || null,
                signature: rec.signature || null, gcSignature: rec.gcSignature || null };
    try {
      if (rec.syncKind === 'production' && typeof window.syncProductionReportToDb === 'function') {
        await window.syncProductionReportToDb(s, submissionId, rec.weekMode || null);
      } else if (rec.syncKind === 'timesheet' && typeof window.syncTimesheetToDb === 'function') {
        await window.syncTimesheetToDb(s, submissionId, rec.weekMode || null);
      } else if (rec.syncKind === 'ewo' && typeof window.syncExtraWorkOrderToDb === 'function') {
        // EWO: parent + labour/materials + photos, number assigned here at replay.
        await window.syncExtraWorkOrderToDb(s, submissionId);
      }
    } catch (e) {
      // RETHROW. This used to be swallowed with "(submission is saved)" — true
      // of the Activity-log row, and beside the point: the structured half is
      // the hours, the installed quantity and the extra-work lines, i.e. every
      // number the owner actually reads. Swallowing it meant flushOutbox went
      // on to delete the record, which is the ONLY copy of that work, while
      // the foreman was told "N saved form(s) sent".
      //
      // Throwing hands the record back to flushOutbox's own retry: attempts++,
      // and after OFFLINE_MAX_ATTEMPTS it is flagged `stuck` — kept, and
      // surfaced — instead of discarded.
      console.warn('[offline] structured sync failed on replay — keeping the record queued:', e);
      throw e;
    }
  }

  // 3. Email — safety inbox only, and best-effort. The online path emails just
  // the forms in SAFETY_EMAIL_TYPES (app.js); production, timesheets, EWO and
  // the pack logs are stored + synced silently. The replay has to make the same
  // call, or a queued form would mail the safety officer something they never
  // receive when the same form is submitted with signal.
  const _emailsSafety = typeof _isSafetyEmailForm === 'function'
    ? _isSafetyEmailForm(rec.submissionType)
    : true;   // app.js absent (shouldn't happen) — keep the old behaviour
  if (!_emailsSafety) return;
  try {
    await submitForm({
      foremanName:    rec.userName || rec.foremanName,
      project:        rec.project,
      submissionType: rec.submissionType,
      date:           rec.date,
      workersOnSite:  rec.workersOnSite,
      sendToSafety:   rec.sendToSafety,
      _csoContact:    rec.csoContact,
    }, pdfBuffer, [], submissionId);
  } catch (e) {
    console.warn('[offline] email send failed on replay (row is saved — not re-queuing):', e);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Flush the outbox (on reconnect + after login). Guarded so it only runs once
//  at a time and only when we have both a connection and an authenticated user
//  (writes need the session for RLS).
// ═══════════════════════════════════════════════════════════════════════════════
let _flushing = false;
let _flushAgain = null;   // the options of a flush asked for while one was running: it runs once more after it
let _flushDone = null;    // the running flush, its run-again included: what a call made meanwhile waits for
// ── Sync-only repair records ─────────────────────────────────────────────────
// The submission and its PDF are already in the database; what failed was the
// second write — the per-day money rows. So this replay is ONLY that second
// write, through the SAME sync functions the online path uses ('mine' scoping
// makes a re-run idempotent). Throws on failure exactly like a full replay, so
// flushOutbox keeps the record and retries on the next trigger.
async function _replaySyncOnly(rec) {
  if (!rec.submissionId) throw new Error('sync-only record has no submission id');
  const s = { project: rec.project, date: rec.date, fields: rec.fields || {},
              _effForemanId: rec.userId, _effForemanName: rec.userName,
              _crewForemanId: rec.crewForemanId || null,
              _crewForemanName: rec.crewForemanName || '' };
  if (rec.syncKind === 'production' && typeof window.syncProductionReportToDb === 'function') {
    await window.syncProductionReportToDb(s, rec.submissionId, rec.weekMode || null);
  } else if (rec.syncKind === 'timesheet' && typeof window.syncTimesheetToDb === 'function') {
    await window.syncTimesheetToDb(s, rec.submissionId, rec.weekMode || null);
  } else {
    throw new Error('sync-only replay: unknown kind ' + rec.syncKind);
  }
}

// Queue the second write for an automatic retry. `sLike` carries what the sync
// functions read (project, date, fields, effective foreman, crew) — from live
// state when an online sync fails, or rebuilt from submissions.data by the
// watchdog. Never throws into the caller's submit flow.
async function saveSyncRepairOffline(sLike, submissionId) {
  const record = window.Ordinex.offlineRecord.build({
    submissionType: sLike.submissionType,
    project: sLike.project, foremanName: sLike._effForemanName || '',
    date: sLike.date,
    effUserId: sLike._effForemanId || (window.currentUser && window.currentUser.id) || null,
    effUserName: sLike._effForemanName || '',
    crewForemanId: sLike._crewForemanId || null,
    crewForemanName: sLike._crewForemanName || '',
    orgId: (window.currentOrg && window.currentOrg.id) || null,
    pdf: null, pdfFilename: '', signed: false,
    syncKind: sLike.submissionType === 'Production Report' ? 'production' : 'timesheet',
    fields: sLike.fields || {},
    data: {},
    syncOnly: true,
    submissionId,
    weekOf: _weekOfIso(sLike.date),
    // Only the live-submit retry may carry an answer; the watchdog passes null
    // so a rebuild replaces its own rows and nothing else.
    weekMode: sLike.weekMode || null,
  });
  await _outboxAdd(record);
  _bumpRepairAttempts(submissionId);
  // Same wake-up the full offline save registers. Without it a repair queued
  // just before the tab closed waited for a manual app open — the delay the
  // watchdog exists to remove.
  try { _requestBackgroundSync(); } catch (_) { /* unsupported browser */ }
  _renderOfflineUI();
}

// How many times we have queued a rebuild for a submission. Some payloads can
// never write a row (every material renamed, every worker gone): the RPC
// succeeds writing nothing, the record is deleted as "sent", and without this
// the watchdog re-queued the same submission every five minutes forever.
const _REPAIR_TRIES_KEY = 'ordinex_repair_attempts';
function _repairAttempts(submissionId) {
  try { return (JSON.parse(localStorage.getItem(_REPAIR_TRIES_KEY) || '{}'))[submissionId] || 0; }
  catch { return 0; }
}
function _bumpRepairAttempts(submissionId) {
  if (!submissionId) return;
  try {
    const all = JSON.parse(localStorage.getItem(_REPAIR_TRIES_KEY) || '{}');
    all[submissionId] = (all[submissionId] || 0) + 1;
    localStorage.setItem(_REPAIR_TRIES_KEY, JSON.stringify(all));
  } catch (_) { /* private mode — the cap degrades, nothing breaks */ }
}
window.repairAttemptsFor = _repairAttempts;
window.saveSyncRepairOffline = saveSyncRepairOffline;

// Watchdog dedup, in ONE read of the store. The old shape asked per
// submission, and each ask deserialised every queued record — PDFs included —
// to compare two scalars. It also only matched sync-only records, so a FULL
// queued record (its submission landed, its structured half threw) was
// invisible and the watchdog queued a second repair for the same week.
async function outboxPendingSync() {
  const out = { bySubmission: new Set(), byWeek: new Set(), byFiling: new Set() };
  try {
    for (const r of await _outboxAll()) {
      if (!_isMine(r)) continue;   // another person's queue never blocks the repair of this user's own filing
      if (r.submissionId) out.bySubmission.add(r.submissionId);
      const wk = _weekOfKey(r.weekOf);
      if (r.syncKind && r.project && wk) out.byWeek.add(r.project + '|' + wk);
      // The week-collision gate's key (type|project|crew|Monday) — so an
      // OFFLINE re-file of a week already queued here still gets the question.
      const kind = r.syncKind === 'production' ? 'Production Report'
                 : r.syncKind === 'timesheet'  ? 'Weekly Timesheet' : null;
      if (kind && r.project && wk) {
        out.byFiling.add([kind, r.project, (kind === 'Production Report' && r.crewForemanId) || r.userId, wk].join('|'));
      }
    }
  } catch { /* unreadable store — the watchdog just re-checks next time */ }
  return out;
}
window.outboxPendingSync = outboxPendingSync;

// What the clock card and the day sheets lay over the server's rows while the outbox still
// holds clock work: the punches still to go, in the order they replay in, and the time-off
// requests. A punch the server refused for good is stuck and no longer part of anybody's day,
// and neither are the later punches of its worker, held back behind it (see flushOutbox): a
// worker with a stuck punch has none of his queued punches in the day yet.
// Only the signed-in user's queue: on a shared phone the card must never show what another
// person tapped. An unreadable store reads as nothing queued.
async function _outboxPendingClock() {
  let all = [];
  try { all = await _outboxAll(); } catch { /* the server's rows alone */ }
  const recs = all.filter(_isMine);
  const held = _punchHold(recs).workers;
  const punches = recs.filter(r => r.syncKind === 'punch' && !held.has(r.workerId));
  return {
    punches: window.Ordinex.offlineRecord.orderPunches(punches).map(r => r.punch),
    // a request the server refused for good carries its words (the Time off row says them, not "sending…")
    timeoff: recs.filter(r => r.syncKind === 'timeoff').map(r => (r.waiting === 'foreman' ? { ...r.request, refused: r.waitingText } : r.request)),
    // a queued Approve / Deny: the request it decides is no longer pending for this phone
    decisions: recs.filter(r => r.syncKind === 'timeoff_decision').map(r => ({ requestId: r.requestId, decision: r.decision })),
  };
}
window._outboxPendingClock = _outboxPendingClock;

async function flushOutbox(opts = {}) {
  // One flush at a time. One asked for meanwhile is not dropped: it runs once more after this one, so a
  // pill tap or a flush of the forms never gets lost behind a punches-only retry; and its caller waits
  // for that run, so `await flushOutbox()` still means its records were tried. (Never awaited from
  // inside a flush: it would wait for itself.)
  if (_flushing) { _flushAgain = _flushAgain ? _mergeFlushOpts(_flushAgain, opts) : { ...opts }; return _flushDone; }
  if (!window.currentUser) return;
  // Booted offline: nothing to send, but the pill drawn before sign-in knew no owner yet.
  if (!navigator.onLine) { _renderOfflineUI(); return; }
  _flushing = true;   // before the read: two flushes must never read, and send, the same records
  _flushDone = (async () => {
    try {
      await _flushRun(opts);
    } finally {
      _flushing = false;
    }
    const again = _flushAgain;
    _flushAgain = null;
    if (again) await flushOutbox(again);
  })();
  return _flushDone;
}

// Two flushes asked for as one: the toasts if either wanted them, the stuck records if either asked
// for them, the forms unless both were punches-only.
function _mergeFlushOpts(a, b) {
  return {
    toast: a.toast === false && b.toast === false ? false : undefined,
    includeStuck: !!(a.includeStuck || b.includeStuck),
    punchesOnly: !!(a.punchesOnly && b.punchesOnly),
  };
}

async function _flushRun(opts) {
  let all;
  try { all = await _outboxAll(); } catch { return; }
  // The outbox belongs to the device, the session to a person: records replay under whoever is
  // signed in now, and the server stamps punched_by from that session — so what one person
  // queued must never go out under another's name (shared phone: A queues, signs out, B signs
  // in). Other people's records wait, untouched (no attempt, not stuck, not counted anywhere),
  // for their own user. A record with no userId predates the field and goes out as before.
  const items = all.filter(_isMine);
  if (!items.length) { _renderOfflineUI(); _schedulePunchRetry(); return; }

  let sent = 0, failed = 0, stuck = 0, stuckPunches = 0, closed = 0;
  let replayedPunch = false;   // a punch went to the server: the clock surfaces read it again after
  let replayedTimeOff = false; // a time-off record was sent (landed or refused): the Time off page paints again
  let replayedDelivery = false; // a delivery or pickup was tried: its list leaves "waiting to send"
  let lineDown = false;        // the line failed on a punch: no more punches this flush (the schedule retries)
  // A manual pill tap retries stuck records too (support may have fixed the
  // cause; a deliberate tap is explicit intent). Auto-flushes on reconnect/
  // login still skip them so a permanently broken record can't spin forever.
  // A punch that waits for its foreman is stuck too, so the same tap retries it
  // (the foreman may have fixed the day) — from a clean slate.
  const sendable = opts.includeStuck
    ? items.map(r => (r.stuck
        ? Object.assign(r, { stuck: false, attempts: 0, waiting: null, waitingText: null, waitingPill: null })
        : r))
    : items.filter(r => !r.stuck);
  // Clock punches stay out of the form counts and toasts: the card on screen is their feedback.
  // So does a sheet waiting for the office (week_closed): it is tried on every flush, but it is
  // not news each time.
  // The retry schedule of punches (punchesOnly) leaves the forms to their own triggers.
  const others = opts.punchesOnly ? [] : sendable.filter(r => r.syncKind !== 'punch');
  // Time-off requests and decisions are clock records too: the Time off page's row is their feedback.
  const announced = others.filter(r => !['timeoff','timeoff_decision'].includes(r.syncKind) && r.waiting !== 'week_closed').length;
  // What the toasts call them: forms, delivery requests (js/delivery.js), or a mix of both ('items').
  const formRecs = others.filter(r => !['timeoff','timeoff_decision'].includes(r.syncKind));
  const dl = formRecs.filter(r => r.syncKind === 'delivery').length;
  const what = n => (!dl ? 'form' : dl === formRecs.length ? 'request' : 'item') + (n > 1 ? 's' : '');
  if (opts.toast !== false && announced) {
    _toast(`Sending ${announced} saved ${what(announced)}…`, 'info');
  }
  // A worker whose punch fails waits: his later punches stay for the next flush, because an
  // `out` sent ahead of its failed `in` would be refused as not_in and read as final. A STUCK
  // punch holds its worker back the same way in every flush, not only the one that stuck it:
  // an Undo (void) sent alone finds its check-in missing and is dropped as "already handled",
  // and the check-in it undid lands later. So the workers of the stuck punches start out
  // skipped. A pill tap (includeStuck) resets the stuck ones: they replay first, in order, and
  // a failure holds the rest, as in any flush. Other workers go on.
  const skipWorkers = opts.includeStuck ? new Set() : _punchHold(items).workers;
  try {
    // Punches first, each worker's in the order he made them; then the rest as queued.
    // With no punch queued the loop needs nothing from the core bundle: forms flush as before.
    const queued = sendable.filter(r => r.syncKind === 'punch');
    const punches = queued.length ? window.Ordinex.offlineRecord.orderPunches(queued) : [];
    for (const rec of [...punches, ...others]) {
      const isPunch = rec.syncKind === 'punch';
      const isClock = ['punch','timeoff','timeoff_decision'].includes(rec.syncKind);   // never counted as a form
      // A tap's batch on its way (js/app.js punchMany) writes each punch here before it sends it: that copy
      // is left to the batch, which takes it out once answered or flushes it when it stays queued; the
      // worker's later punches wait behind it, as behind any punch not sent.
      if (isPunch && window.__punchesSending?.has(rec.punch?.id)) skipWorkers.add(rec.workerId);
      if (isPunch && (lineDown || skipWorkers.has(rec.workerId))) continue;
      rec.waiting = null;   // every attempt decides again whether the record waits
      if (isPunch) replayedPunch = true;
      if (!isPunch && isClock) replayedTimeOff = true;   // landed or refused: either way its row changes
      if (rec.syncKind === 'delivery') replayedDelivery = true;
      try {
        await _replayRecord(rec);
        await _outboxDelete(rec.id);
        if (!isClock) sent++;
      } catch (e) {
        console.warn('[offline] replay failed, will retry later:', e);
        if (isPunch) skipWorkers.add(rec.workerId);
        const p = e && e.punch;
        if (p && p.action === 'stuck_now') {
          // Only a foreman can fix it: keep the punch (it is the worker's hour), say so once,
          // and never count it against the ten tries — the pill, not "contact support", tells it.
          rec.stuck = true;
          rec.waiting = 'foreman';
          rec.waitingText = p.waitingText;
          rec.waitingPill = p.pillText;
          if (p.waitingText) _toast(p.waitingText, 'warning');
        } else if ((p && p.action === 'wait') || /week_closed/.test((e && e.message) || '')) {
          rec.waiting = 'week_closed';   // the office closed that week: kept, no attempt counted
          if (opts.includeStuck) closed++;   // a deliberate tap is told why nothing went
        } else if (isClock && _isNetworkError(e)) {
          // the line failed, not the punch: no try counted. It goes again on the retry schedule, and an
          // hour of signal without data must not turn it into "contact support"
          lineDown = true;
        } else if (isClock && p && p.server) {
          // the server failed, not the punch (a 5xx, a gateway's error page, a statement timeout): no try
          // counted either, and an hour of an outage must not turn it into "contact support". Only this
          // worker's later punches wait (skipWorkers, above): a punch the server chokes on never starves the
          // others, which it would if the whole flush stopped like it does for a lost line
        } else {
          rec.attempts = (rec.attempts || 0) + 1;
          if (rec.attempts >= OFFLINE_MAX_ATTEMPTS) {
            rec.stuck = true;   // stop retrying, keep the data, surface it
            stuck++;
            if (isPunch) stuckPunches++;
          }
        }
        try { await _outboxPut(rec); } catch { /* bookkeeping is best-effort */ }
        if (!isClock && !rec.waiting) failed++;   // a failure that will be retried, and is worth saying
        if (!navigator.onLine) break;   // connection dropped mid-flush — stop
      }
    }
  } finally {
    _renderOfflineUI();
    // Punches went out (or were refused): the clock cards read the server again (js/app.js), or
    // the next repaint of Today would trust a clock context fetched before they landed.
    if (replayedPunch && typeof _clockRefreshSurfaces === 'function') {
      _loadClockContext().then(() => _clockRefreshSurfaces());
    }
    if (replayedTimeOff && typeof renderTimeOffPage === 'function') renderTimeOffPage();   // paints only while it is open
    if (replayedDelivery && typeof loadForemanDeliveries === 'function') loadForemanDeliveries();
    _schedulePunchRetry();
  }
  // First: one toast element shows the last call, so a "contact support" after it is never hidden.
  // The sentence is the field app's (js/app.js, loaded on the same page), never a second copy.
  if (closed) _toast(_tsWeekClosedMsg(), 'warning');
  if (sent)  _toast(`${sent} saved ${what(sent)} sent`, 'success');
  if (stuck) {
    // a group of punches only says punch(es), a mixed group keeps the form(s) wording
    const word = stuck === stuckPunches ? (stuck > 1 ? 'punches' : 'punch') : what(stuck);
    _toast(`${stuck} ${word} could not be sent after ${OFFLINE_MAX_ATTEMPTS} tries — contact support`, 'error');
  }
  else if (failed && navigator.onLine) _toast(`${failed} ${what(failed)} still pending — will retry`, 'warning');
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Punches still on their way while the line is up
// ═══════════════════════════════════════════════════════════════════════════════
// A punch queued during a blip that never flipped navigator.onLine would otherwise wait for the next
// `online` event or the next sign-in. While the line is up and this user still has punches on their
// way (not stuck, not held behind a stuck one, not waiting for the office to reopen a week), the outbox
// goes again: 5 s, 15 s, 30 s, 60 s, then every 2 min, from the end of each flush; and whenever the app
// comes back to the front.
const _PUNCH_RETRY_MS = [5000, 15000, 30000, 60000, 120000];
let _punchRetryStep = 0;
let _punchRetryTimer = null;
async function _schedulePunchRetry() {
  clearTimeout(_punchRetryTimer);
  _punchRetryTimer = null;
  if (!(await _punchesPending())) { _punchRetryStep = 0; return; }
  const ms = _PUNCH_RETRY_MS[Math.min(_punchRetryStep++, _PUNCH_RETRY_MS.length - 1)];
  _punchRetryTimer = setTimeout(() => flushOutbox({ toast: false, punchesOnly: true }), ms);
}
async function _flushPunchesIfPending() {
  if (await _punchesPending()) flushOutbox({ toast: false, punchesOnly: true });
}
async function _punchesPending() {
  if (!navigator.onLine || !window.currentUser) return false;
  try {
    const recs = (await _outboxAll()).filter(_isMine);
    const { isHeld } = _punchHold(recs);
    return recs.some(r => r.syncKind === 'punch' && !r.stuck && !r.waiting && !isHeld(r));
  } catch { return false; }   // an unreadable store: nothing to retry
}

// ═══════════════════════════════════════════════════════════════════════════════
//  UI: offline banner + pending-outbox pill + header dot
// ═══════════════════════════════════════════════════════════════════════════════
function _toast(msg, type) {
  try { if (typeof showToast === 'function') showToast(msg, type); } catch {}
}

function _ensureOfflineEls() {
  let banner = document.getElementById('offlineBanner');
  if (!banner) {
    banner = document.createElement('div');
    banner.id = 'offlineBanner';
    banner.className = 'offline-banner';
    banner.hidden = true;
    document.body.appendChild(banner);
    // Keep --offline-banner-h in sync with the banner's real rendered
    // height (text can wrap to 2 lines on a narrow phone) so the
    // body.ordinex-offline padding-top in styles.css never underestimates
    // it and leaves the fixed banner overlapping page content/header.
    if (typeof ResizeObserver === 'function') {
      // Read offsetHeight inside the callback rather than entry.contentRect —
      // contentRect excludes the banner's own padding, which under-measures
      // by exactly that padding and left the compensating page offset short.
      new ResizeObserver(() => {
        document.documentElement.style.setProperty('--offline-banner-h', `${Math.ceil(banner.offsetHeight)}px`);
      }).observe(banner);
    }
  }
  let pill = document.getElementById('outboxPill');
  if (!pill) {
    pill = document.createElement('button');
    pill.id = 'outboxPill';
    pill.type = 'button';
    pill.className = 'outbox-pill';
    pill.hidden = true;
    // punches waiting for the foreman open their sheet; anything else is sent again
    pill.onclick = async () => { if (!(await openPunchWaitSheet())) flushOutbox({ includeStuck: true }); };
    document.body.appendChild(pill);
  }
  return { banner, pill };
}

async function _renderOfflineUI() {
  const { banner, pill } = _ensureOfflineEls();
  const online = navigator.onLine !== false;

  let all = [];
  try { all = await _outboxAll(); } catch { /* an unreadable store reads as empty */ }
  // Only what the signed-in user can send: another person's queue waits for them (see
  // flushOutbox) and is none of this user's business. A record with no userId predates the
  // field and counts for whoever is signed in; with nobody signed in yet (the page has just
  // loaded) only those show, and the first flush after sign-in draws the pill again.
  const recs = all.filter(_isMine);
  const count = recs.length;

  banner.hidden = online;
  if (!online) {
    banner.innerHTML =
      '<span class="offline-banner-dot"></span> Offline — forms, deliveries and pickups are saved on this device and will send when you reconnect.';
  }
  // Push page content down so the fixed banner never hides the header.
  if (document.body) document.body.classList.toggle('ordinex-offline', !online);

  // Records the person can do nothing about by waiting, told apart by who can fix them:
  //   stuck    gave up after OFFLINE_MAX_ATTEMPTS — support
  //   foreman  a punch refused for good (kept `stuck`, with its own text) — the foreman — and
  //            the later punches of its worker, held back behind it (see flushOutbox): they
  //            wait with it, they are not "sending"
  //   closed   a sheet the office closed that week — the office, by reopening it
  // Everything else is on its way: `pending`.
  const { isHeld } = _punchHold(recs);
  const pending = recs.filter(r => !r.stuck && !r.waiting && !isHeld(r));
  const stuckList = recs.filter(r => r.stuck && !r.waiting);
  const foremanList = recs.filter(r => r.waiting === 'foreman');
  const foremanCount = foremanList.length + recs.filter(isHeld).length;
  const closedList = recs.filter(r => r.waiting === 'week_closed');
  const pendingPunches = pending.filter(r => ['punch','timeoff','timeoff_decision'].includes(r.syncKind)).length;   // clock records: "N sending"
  const pendingForms = pending.length - pendingPunches;
  const s = n => (n > 1 ? 's' : '');
  // A group made only of punches says punch(es) where the text says form(s) / sheet(s); one
  // with delivery requests (js/delivery.js) says request(s), or item(s) mixed with forms.
  const noun = (list, other) => {
    const dl = list.filter(r => r.syncKind === 'delivery').length;
    const word = list.every(r => r.syncKind === 'punch') ? 'punch' : !dl ? other : dl === list.length ? 'request' : 'item';
    return list.length > 1 ? (word === 'punch' ? 'punches' : word + 's') : word;
  };
  const pendingFormRecs = pending.filter(r => !['punch','timeoff','timeoff_decision'].includes(r.syncKind));

  pill.hidden = count === 0;
  if (count > 0) {
    if (stuckList.length > 0) {
      pill.textContent = `${stuckList.length} ${noun(stuckList, 'form')} stuck — contact support`;
      pill.disabled = false;   // clicking retries (support may have fixed the cause)
    } else if (foremanCount > 0) {
      // the foreman's own punches for his crew (crew or correction): nobody to wait for, he is the one to look
      pill.textContent = [...foremanList, ...recs.filter(isHeld)].every(_isCrewPunch)
        ? `${foremanCount} crew punch${foremanCount > 1 ? 'es' : ''} could not be saved — tap to see`
        : foremanCount > 1 && foremanList.every(r => r.syncKind === 'timeoff')
          ? `${foremanCount} time off requests refused — see Time off`
        : foremanCount > 1
          ? `${foremanCount} punches need your foreman`
          : (foremanList[0]?.waitingPill || '1 punch needs your foreman');
      pill.disabled = false;   // clicking retries (the foreman may have fixed the day)
    } else if (closedList.length > 0) {
      pill.textContent = `${closedList.length} ${noun(closedList, 'sheet')} waiting — the office closed that week`;
      pill.disabled = false;   // clicking retries (the office may have reopened the week)
    } else {
      // Forms keep their wording; punches say "sending" online and offline alike (the
      // offline banner already says "Offline").
      pill.textContent = [
        pendingForms > 0 && (online
          ? `Sending ${pendingForms} saved ${noun(pendingFormRecs, 'form')}…`
          : `${pendingForms} ${noun(pendingFormRecs, 'form')} waiting to send`),
        pendingPunches > 0 && `${pendingPunches} sending`,
      ].filter(Boolean).join(' · ');
      pill.disabled = online;   // online ⇒ auto-sending; offline ⇒ informational
    }
  }

  const dot = document.getElementById('backendStatus');
  if (dot) {
    dot.classList.toggle('offline', !online);
    dot.title = online ? 'Online' : 'Offline — forms saved locally';
  }

  // Redesign v3 — the connection pill in the Today app bar. Same three facts
  // the banner and the outbox pill carry, said in the header where the mock
  // puts them: OFFLINE while there is no line, "N QUEUED" while the outbox
  // still holds work, and nothing at all on a normal connected day.
  const net = document.getElementById('fxNetPill');
  if (net) {
    if (!online) {
      net.hidden = false;
      net.className = 'fx-net fx-net--off';
      net.textContent = 'Offline';
      net.title = 'Offline — forms, deliveries and pickups are saved on this device and send when you reconnect';
    } else if (pending.length > 0) {
      // Only what is really on its way: a punch waiting for its foreman stays queued for days
      // and a closed week for as long as the office takes — neither reads "sending" in the header.
      net.hidden = false;
      net.className = 'fx-net fx-net--queue';
      net.textContent = `${pending.length} sending`;
      net.title = `${pending.length} saved item${s(pending.length)} still being sent`;
    } else {
      net.hidden = true;
      net.textContent = '';
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Punches waiting for the foreman — the sheet behind the pill
// ═══════════════════════════════════════════════════════════════════════════════
let _punchWaitOpener = null;   // what had the focus when the sheet opened (the pill): it gets it back when the sheet closes
// A punch the server refused for good waits for the foreman to fix the day, and holds back its
// worker's later punches (see flushOutbox). The pill counts them; its tap lists them and sends
// them again ("Try again": the foreman may have fixed it) or, after a confirm, drops them: spec 8
// forbids dropping a punch in silence, and without this a punch nobody can land would freeze that
// worker's offline punches for good. Opens only while the pill shows that group (the groups of
// _renderOfflineUI: a record stuck for support is shown first, and its tap retries as before).
// Resolves false when it opened nothing.
async function openPunchWaitSheet() {
  await _flushSettled();   // a send on its way (Try again) may still be writing them: show them as it leaves them
  let recs;
  try { recs = (await _outboxAll()).filter(_isMine); } catch { return false; }
  if (recs.some(r => r.stuck && !r.waiting)) return false;
  const { isHeld } = _punchHold(recs);
  const waiting = window.Ordinex.offlineRecord.orderPunches(recs.filter(r => r.syncKind === 'punch'
    && (r.waiting === 'foreman' || isHeld(r))));
  const sheet = document.getElementById('punchWaitSheet');
  if (!waiting.length || !sheet) return false;
  const KIND = { in: 'Check in', out: 'Check out', break_start: 'Start break', break_end: 'Resume work', void: 'Undo' };
  const span = (className, textContent) => Object.assign(document.createElement('span'), { className, textContent });
  const title = document.getElementById('punchWaitTitle');
  if (title) {
    title.textContent = waiting.every(_isCrewPunch)
      ? `${waiting.length} crew punch${waiting.length > 1 ? 'es' : ''} could not be saved`
      : 'Punches waiting for your foreman';
  }
  document.getElementById('punchWaitList').replaceChildren(...waiting.map(r => {
    const row = Object.assign(document.createElement('div'), { className: 'fx-row fx-row--static' });
    const txt = span('fx-row-txt', '');
    txt.append(span('fx-row-name', `${KIND[r.punch.kind] || 'Punch'} ${window.Ordinex.timeClock.fmtTime(r.punchedAt)} — ${r.projectName || ''}`
      + (_isCrewPunch(r) && r.workerName ? ` · ${r.workerName}` : '')));
    if (r.waitingText) txt.append(span('fx-row-sub', r.waitingText));
    row.append(txt);
    return row;
  }));
  document.getElementById('punchWaitRetry').onclick = () => { closePunchWaitSheet(); flushOutbox({ includeStuck: true }); };
  document.getElementById('punchWaitDiscard').onclick = async () => {
    const n = waiting.length, what = n > 1 ? `${n} punches` : '1 punch';
    if (!confirm(`Discard ${what}? ${n > 1 ? 'They' : 'It'} will not be saved.`)) return;
    await _flushSettled();   // a send that read them first would write them back after the delete
    for (const r of waiting) await _outboxDelete(r.id);
    closePunchWaitSheet();
    _toast(`${what} discarded`, 'info');
    _renderOfflineUI();
    if (typeof _clockRefreshSurfaces === 'function') _clockRefreshSurfaces();   // the cards read the outbox again
  };
  if (sheet.style.display !== 'flex') _punchWaitOpener = document.activeElement;   // not when an open sheet is asked for again
  sheet.style.display = 'flex';
  sheet.querySelector('[role="dialog"]').focus();   // the dialog itself (tabindex -1): a screen reader reads its title, the next Tab is its first control
  return true;
}
async function _flushSettled() {
  while (_flushing) await new Promise(resolve => setTimeout(resolve, 50));
}
function closePunchWaitSheet() {
  document.getElementById('punchWaitSheet').style.display = 'none';
  _punchWaitOpener?.focus();   // a pill that is gone or hidden by now (the last punch was discarded) simply takes no focus
  _punchWaitOpener = null;
}

// ═══════════════════════════════════════════════════════════════════════════════
//  "Saved offline" success screen (mirrors app.js showSuccessScreen)
// ═══════════════════════════════════════════════════════════════════════════════
// `why`: the server's refusal in words (app.js handleSubmit) — it replaces "when you have signal".
function showOfflineSavedScreen(pdfFilename, why = null) {
  // Queued is a finished form too: the 30-second auto-save must not keep
  // writing a draft for work that is already sitting in the outbox.
  if (typeof _stopAutoSaveDraft === 'function') _stopAutoSaveDraft();
  const panel = document.getElementById('step4');
  if (!panel) return;
  // Same rule as the online Done screen: the wizard's head and its action bar
  // step aside, or "Submit Form" sits under a screen that says it is saved.
  const _fh = document.getElementById('fillHead');
  if (_fh) _fh.style.display = 'none';
  const _nb = document.querySelector('.nav-bar');
  if (_nb) _nb.style.display = 'none';
  // Not sent yet, so nothing here says "filed": the PDF downloaded to this phone
  // is the copy until the outbox sends it. Same two ways on as the online screen.
  const t = window.Ordinex.i18n.t, esc = _fieldEsc;   // app.js's escape helper
  panel.innerHTML = `
    <div class="success-screen success-screen--offline">
      <div class="success-icon"><i data-lucide="cloud-off"></i></div>
      <h2 class="success-title">${esc(t('done.offlineTitle'))}</h2>
      <p class="success-msg">${esc(why || t('done.offlineMsg'))}</p>
      ${pdfFilename ? `<div class="success-filed"><i data-lucide="smartphone"></i>${esc(t('done.localCopy'))} <b>${esc(pdfFilename)}</b></div>` : ''}
      <button class="btn btn-next" onclick="startAnotherForm()" style="margin-top:24px;width:100%">Start another form</button>
      <button class="btn btn-back" onclick="resetApp()" style="margin-top:10px;width:100%">Back to Today</button>
    </div>`;
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Install invitation (mobile Track A, wave 3)
//  The app has been installable for months — manifest, icons, standalone — but
//  nothing ever SAID so, and "dig into the browser menu" is not a thing field
//  crews do. Android fires beforeinstallprompt (we stash it and offer a real
//  one-tap install); iOS has no such event, so the same card opens a short
//  "Share → Add to Home Screen" sheet instead. Once dismissed, never again.
// ═══════════════════════════════════════════════════════════════════════════════
const _INSTALL_DISMISS_KEY = 'ordinexInstallPromptDismissed';
let _deferredInstall = null;   // the stashed beforeinstallprompt event (Android)
let _installCardShown = false;

function _isStandalone() {
  try {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone === true;   // old-style iOS Safari flag
  } catch (_) { return false; }
}
function _isIOS() {
  const ua = navigator.userAgent || '';
  // iPadOS 13+ reports as Mac; the touch check tells them apart.
  return /iPhone|iPad|iPod/i.test(ua) ||
         (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
}
function _installDismissed() {
  try { return localStorage.getItem(_INSTALL_DISMISS_KEY) === '1'; } catch (_) { return false; }
}
function _dismissInstall() {
  try { localStorage.setItem(_INSTALL_DISMISS_KEY, '1'); } catch (_) {}
  const card = document.getElementById('installCard');
  if (card) card.remove();
  const sheet = document.getElementById('installSheet');
  if (sheet) sheet.remove();
}

// All the reasons NOT to invite, in one testable place. Phone-shaped screens
// only (the owner works the admin panel on a PC all day — no nagging there),
// logged in, on the form picker with nothing half-filled, not already
// installed, not previously dismissed, and — outside iOS — only when the
// browser actually offered an install (beforeinstallprompt fired).
function _installEligible() {
  if (_isStandalone() || _installDismissed()) return false;
  if (!window.currentUser) return false;
  try { if (typeof state !== 'undefined' && state.submissionType) return false; } catch (_) {}
  try { if (!window.matchMedia('(pointer: coarse)').matches) return false; } catch (_) { return false; }
  return _isIOS() || !!_deferredInstall;
}

function _maybeShowInstallCard() {
  if (_installCardShown || !_installEligible()) return;
  _installCardShown = true;
  const card = document.createElement('div');
  card.id = 'installCard';
  card.className = 'install-card';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-label', 'Install Ordinex');
  card.innerHTML = `
    <img class="install-card-icon" src="assets/app-icon/app-icon-192.png?v=539" alt="" width="44" height="44">
    <div class="install-card-text">
      <b>Install Ordinex</b>
      <span>Opens instantly from your home screen — works with no signal.</span>
    </div>
    <div class="install-card-actions">
      <button type="button" class="install-card-btn" id="installCardGo">${_isIOS() && !_deferredInstall ? 'How' : 'Install'}</button>
      <button type="button" class="install-card-dismiss" id="installCardNo" aria-label="Not now">Not now</button>
    </div>`;
  document.body.appendChild(card);
  document.getElementById('installCardNo').onclick = _dismissInstall;
  document.getElementById('installCardGo').onclick = async () => {
    if (_deferredInstall) {
      // Real one-tap install (Android / desktop Chrome).
      const ev = _deferredInstall;
      _deferredInstall = null;
      card.remove();
      try {
        ev.prompt();
        const choice = await ev.userChoice;
        if (choice && choice.outcome === 'accepted') _dismissInstall();
        else _installCardShown = false;            // declined the OS dialog — card may return next visit
      } catch (_) { _installCardShown = false; }
    } else {
      _showIOSInstallSheet();
    }
  };
}

function _showIOSInstallSheet() {
  if (document.getElementById('installSheet')) return;
  const sheet = document.createElement('div');
  sheet.id = 'installSheet';
  sheet.className = 'install-sheet';
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-label', 'Add Ordinex to your home screen');
  sheet.innerHTML = `
    <div class="install-sheet-panel">
      <h3>Add Ordinex to your home screen</h3>
      <ol>
        <li>Tap the <b>Share</b> button <i data-lucide="share"></i> in the browser bar</li>
        <li>Scroll and tap <b>Add to Home Screen</b> <i data-lucide="plus-square"></i></li>
        <li>Tap <b>Add</b> — Ordinex appears like any other app</li>
      </ol>
      <button type="button" class="btn btn-next" id="installSheetDone">Got it</button>
    </div>`;
  document.body.appendChild(sheet);
  try { if (window.lucide) lucide.createIcons(); } catch (_) {}
  document.getElementById('installSheetDone').onclick = _dismissInstall;
  sheet.addEventListener('click', (e) => { if (e.target === sheet) sheet.remove(); });
}

function _initInstallInvite() {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();               // we choose the moment, not the browser
    _deferredInstall = e;
    _maybeShowInstallCard();
  });
  window.addEventListener('appinstalled', _dismissInstall);
  // iOS never fires the event, and login lands after boot — re-check calmly.
  let tries = 0;
  const t = setInterval(() => {
    if (++tries > 40 || _installCardShown || _installDismissed() || _isStandalone()) { clearInterval(t); return; }
    _maybeShowInstallCard();
  }, 3000);
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Boot
// ═══════════════════════════════════════════════════════════════════════════════
// Where a deploy's reload loses nothing: no form half-filled (the foreman sits on the form picker),
// no punch on its way — from the tap until its batch has landed or been queued, a punch lives in
// this page (js/app.js punchMany counts them in window.__punchesInFlight) — and no correction half-typed
// in a day sheet (the time, the reason, a break other than the one the sheet filled in), nor the sheet of
// waiting punches open under someone's eyes. The sheets show with display:flex and hide with display:none;
// the values of a closed one linger until the next open resets them, so only an open sheet counts.
function _safeToReload() {
  if (window.__punchesInFlight > 0) return false;
  const shown = id => document.getElementById(id)?.style.display === 'flex';
  const typed = id => document.getElementById(id)?.value ?? '';
  if (shown('punchWaitSheet')) return false;
  // Request delivery / pickup with something typed: its draft is written a moment after the last keystroke.
  if (shown('deliveryPage') && typeof _delivHasContent === 'function' && _delivHasContent()) return false;
  if (shown('clockDaySheet')) {
    const filled = typeof _clockDayCtx !== 'undefined' && _clockDayCtx ? String(_clockDayCtx.breakMinWas ?? '') : '';
    if (typed('clockFixTime') || typed('clockFixReason') || typed('clockBreakMin') !== filled) return false;
  }
  try { return typeof state === 'undefined' || !state.submissionType; }
  catch { return true; }
}

function _registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;

  // ── Auto-update ────────────────────────────────────────────────────────────
  // A PWA that stays open on a phone keeps running the JS it booted with, so a
  // deploy never reaches it until the app is fully closed + reopened. That bit
  // us hard: foremen submitted timesheets whose PDF was built by a stale
  // pdf-generator bundle (no SICK marker) even though the fix was already live.
  //
  // Fix: when a freshly-deployed service worker installs, activate it and reload
  // the page so the newest bundles load — but ONLY at a safe moment. The reload
  // is gated on `state.submissionType` being empty, i.e. the foreman is sitting
  // on the form picker with nothing half-filled, so we can never wipe a form in
  // progress (and never interrupt the post-submit success screen).
  let _reloaded = false;
  let _updatePending = false;
  // If the page was already controlled by a service worker when it loaded, a
  // later controllerchange means a NEW worker took over (a fresh deploy). If it
  // wasn't controlled, the first controllerchange is just the initial install —
  // not an update, so we must not reload on it.
  const _hadControllerAtLoad = !!navigator.serviceWorker.controller;

  const _maybeReload = () => {
    if (_updatePending && !_reloaded && _safeToReload()) {
      _reloaded = true;
      window.location.reload();
    }
  };

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!_hadControllerAtLoad) return;   // initial install, not a new deploy
    _updatePending = true;
    _maybeReload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('sw.js').then(reg => {
      const checkForUpdate = () => { reg.update().catch(() => {}); };

      // Re-check for a new deploy whenever the foreman brings the app forward,
      // and take the chance to land a pending update if they're idle on the picker.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') { checkForUpdate(); _maybeReload(); }
      });
      window.addEventListener('focus', _maybeReload);

      // A new worker finished installing while the old one still controls the
      // page → tell it to take over immediately (sw.js listens for SKIP_WAITING).
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller) {
            nw.postMessage('SKIP_WAITING');
          }
        });
      });

      // Safety net: if an update is waiting and the foreman lingers on the
      // picker, land it within ~30s instead of waiting for the next app open.
      setInterval(_maybeReload, 30000);
    }).catch(e => console.warn('[sw] register failed:', e));
  });
}

function initOffline() {
  // Ask the OS to shield this origin's storage (outbox + draft mirror) from
  // automatic eviction under disk pressure. Best-effort by spec: Chrome
  // decides silently from engagement, Safari may just say no — and a "no"
  // leaves us exactly where we were before the call.
  try { navigator.storage?.persist?.().catch(() => {}); } catch (_) {}
  _initInstallInvite();
  _initSessionMirror();
  // The worker asks an OPEN page to flush (it handles every record type,
  // including the structured ones the worker's REST replay skips).
  try {
    navigator.serviceWorker?.addEventListener('message', (e) => {
      if (e.data === 'FLUSH_OUTBOX') flushOutbox();
    });
  } catch (_) {}
  _renderOfflineUI();
  window.addEventListener('online',  () => { _renderOfflineUI(); flushOutbox(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') _flushPunchesIfPending(); });
  window.addEventListener('offline', () => { _renderOfflineUI(); });
}

_registerServiceWorker();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOffline);
} else {
  initOffline();
}
